import { SdkContext } from '../internal'

/**
 * F-104: the member's e-mail categories, the one-click unsubscribe of one
 * category and the team's "N people will get it" count of the publish forms.
 * The categories always come from the API (its registry); the app keeps no
 * list of its own.
 */

/** One category of the member's preference, labeled by the API in the caller's language. */
export interface EmailCategory {
  key: string
  label: string
  description?: string
  enabled: boolean
  defaultEnabled?: boolean
}

export type EmailShortcut = 'all' | 'campaigns-off' | 'none' | 'custom'

export interface EmailPreferences {
  categories: EmailCategory[]
  shortcut: EmailShortcut
  scope?: string | null
  alwaysSentNote?: string
}

export type EmailPreferencesUpdate =
  | { shortcut: Exclude<EmailShortcut, 'custom'> }
  | { categories: Record<string, boolean> }

/** What a one-click unsubscribe token points at. */
export interface CategoryUnsubscribe {
  communityName: string
  email?: string
  category: { key: string; label: string }
  enabled: boolean
  manageUrl?: string
}

/** The item types that announce themselves by e-mail when published. */
export type NoticeType = 'event' | 'course' | 'callForPapers' | 'survey' | 'post' | 'download' | 'product' | 'episode'

export interface RecipientCountQuery {
  type: NoticeType
  itemId?: string
  spaceId?: string
  visibility?: string
}

export interface RecipientCount {
  type: NoticeType
  category: string
  recipientCount: number
  alreadyNotified: boolean
}

/** `data.emailNotice` of a publish response. */
export interface EmailNoticeResult {
  scheduled: boolean
  recipientCount: number | null
  reason?: string
}

export class EmailNoticesResource {
  constructor(private ctx: SdkContext) {}

  /** The caller's own preference in the community of `X-CommunityId`. */
  async getPreferences(): Promise<EmailPreferences> {
    return this.ctx.call({ httpMethod: 'GET', path: '/members/me/email-preferences' })
  }

  async updatePreferences(body: EmailPreferencesUpdate): Promise<EmailPreferences> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/members/me/email-preferences', hasBody: true }, { body })
  }

  /** Team only (owner, admin, moderator, finance): 403 for anyone else. */
  async recipientCount(query: RecipientCountQuery): Promise<RecipientCount> {
    return this.ctx.call({ httpMethod: 'GET', path: '/content-notices/recipient-count' }, { query })
  }

  /** Public, no session: turns the token's category off. */
  async unsubscribe(token: string): Promise<CategoryUnsubscribe> {
    return this.ctx.call({ httpMethod: 'POST', path: '/public/email-unsubscribe/{token}', hasBody: true }, { token, body: {} })
  }

  /** Public, no session: turns it back on. */
  async undo(token: string): Promise<CategoryUnsubscribe> {
    return this.ctx.call({ httpMethod: 'POST', path: '/public/email-unsubscribe/{token}/undo', hasBody: true }, { token, body: {} })
  }
}
